import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Search, AlertCircle, CheckCircle2, MapPin, Loader2, FileSpreadsheet } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminDiagPlanificacion() {
  const today = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/diag/planificacion-fecha?fecha=${fecha}`);
      setData(data);
      toast.message(`${data.resumen.total_peticiones} peticiones · ${data.resumen.total_lineas} líneas (${data.resumen.disponibles} OK / ${data.resumen.no_disponibles} excluidas)`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const descargarCSV = async () => {
    try {
      const resp = await api.get(`/admin/diag/planificacion-fecha?fecha=${fecha}&formato=csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `diag_planificacion_${fecha}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) {
      toast.error("Error al descargar: " + (e?.message || ""));
    }
  };

  return (
    <div className="space-y-5" data-testid="page-diag-planificacion">
      <div className="flex items-start gap-3">
        <div className="bg-cyan-500/15 border border-cyan-500/30 rounded-lg p-2.5">
          <FileSpreadsheet className="w-6 h-6 text-cyan-300" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-zinc-100">Diagnóstico — ¿Por qué no aparece en Planificación de Rutas?</h1>
          <p className="text-sm text-zinc-400 mt-0.5 leading-snug max-w-3xl">
            Selecciona una fecha y verás TODAS las peticiones con esa <code className="text-cyan-300">fecha_objetivo</code>,
            con un análisis línea-a-línea de las 8 reglas que filtran la pantalla de rutas. Exporta a CSV para compartir.
          </p>
        </div>
      </div>

      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardContent className="pt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Fecha objetivo</label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
              data-testid="input-fecha"
              className="bg-zinc-950 border-zinc-800 w-44" />
          </div>
          <Button onClick={cargar} disabled={loading} data-testid="btn-cargar"
            className="bg-cyan-500 hover:bg-cyan-400 text-zinc-900">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Analizar
          </Button>
          <Button onClick={descargarCSV} variant="outline" data-testid="btn-descargar-csv"
            className="bg-transparent border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10">
            <Download className="w-4 h-4 mr-2" /> Descargar CSV (Excel)
          </Button>
        </CardContent>
      </Card>

      {/* Reglas - referencia rápida */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-300">📋 Reglas que aplica la pantalla de Planificación de Rutas</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-zinc-400 grid md:grid-cols-2 gap-x-6 gap-y-1.5">
          <div><span className="text-amber-300 font-semibold">1. Petición aceptada:</span> estado ∈ {`{aprobada, en_produccion, en_ruta, montado_parcial, entregada}`}</div>
          <div><span className="text-amber-300 font-semibold">2. Tipo de línea válido:</span> {`{montaje, produccion_montaje, retirada, solo_montaje, diseno_produccion_montaje}`}</div>
          <div><span className="text-amber-300 font-semibold">3. Estado línea NO finalizado:</span> ∉ {`{finalizada, incidencia}`}</div>
          <div><span className="text-amber-300 font-semibold">4. NO ya rutada</span> (en cualquier ruta no cancelada)</div>
          <div className="md:col-span-2">
            <span className="text-amber-300 font-semibold">5. Material listo:</span> al menos 1 componente cumple su regla por origen:
            <ul className="ml-4 mt-0.5 text-[11px] text-zinc-500 list-disc list-inside">
              <li>Línea Retirada → componente estado ∈ {`{entregado, recibido_pdv, en_logistica}`}</li>
              <li>Origen <code>cliente_a_pdv</code> (o legacy <code>proveedor_a_cliente</code>) → componente estado ∈ {`{recibido_pdv, entregado}`} → ubicación <b>punto de venta</b> (azul)</li>
              <li>Origen <code>ya_en_pdv</code> → en_logistica/entregado → ubicación punto de venta</li>
              <li>Resto → componente estado ∈ {`{producido, recibido_almacen, cliente_aporta, en_logistica, entregado}`} → ubicación <b>almacén</b> (verde); <code>recibido_pdv</code> → punto de venta</li>
              <li>Fuente única: <code>services/disponibilidad_material.py</code> · línea = completo / parcial X/Y / pendiente</li>
            </ul>
          </div>
          <div><span className="text-amber-300 font-semibold">6. Espacio con coordenadas</span> (lat/lon geocodificadas)</div>
          <div><span className="text-amber-300 font-semibold">7. Solo montaje sin componentes</span> es enrutable automáticamente</div>
        </CardContent>
      </Card>

      {data && (
        <Card className="bg-zinc-900/60 border-zinc-800" data-testid="resultado-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-300 flex items-center justify-between">
              <span>Resultado para {data.resumen.fecha_buscada}</span>
              <div className="flex gap-2 text-xs">
                <Badge className="bg-zinc-800 text-zinc-200">{data.resumen.total_peticiones} peticiones</Badge>
                <Badge className="bg-zinc-800 text-zinc-200">{data.resumen.total_lineas} líneas</Badge>
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">{data.resumen.disponibles} OK</Badge>
                <Badge className="bg-red-500/20 text-red-300 border-red-500/30">{data.resumen.no_disponibles} excluidas</Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.peticiones.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 text-sm">
                Ninguna petición con <code className="text-cyan-300">fecha_objetivo</code> = {data.resumen.fecha_buscada}.
              </div>
            ) : (
              <div className="space-y-4">
                {data.peticiones.map((pet) => (
                  <div key={pet.codigo} className="bg-zinc-950 border border-zinc-800 rounded p-3"
                    data-testid={`peticion-row-${pet.codigo}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="text-sm">
                        <Link to={`/peticiones?codigo=${pet.codigo}`} target="_blank"
                          className="text-amber-300 hover:underline font-medium">{pet.codigo}</Link>
                        <span className="ml-2 text-zinc-200">{pet.nombre}</span>
                        <span className="ml-2 text-zinc-500 text-xs">{pet.cliente}</span>
                      </div>
                      <Badge className={`text-[10px] ${
                        pet.estado === "aprobada" || pet.estado === "en_produccion" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" :
                        pet.estado === "borrador" ? "bg-zinc-700 text-zinc-300" : "bg-amber-500/20 text-amber-300 border-amber-500/30"
                      }`}>{pet.estado}</Badge>
                    </div>
                    <div className="space-y-1.5">
                      {pet.lineas.map((ln) => (
                        <div key={ln.linea_id}
                          className={`text-xs border-l-2 pl-2 py-1 ${ln.aparece_disponible === "SÍ"
                            ? "border-emerald-500/50 bg-emerald-500/[0.04]"
                            : "border-red-500/50 bg-red-500/[0.04]"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="text-zinc-200">
                                {ln.aparece_disponible === "SÍ"
                                  ? <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-400" />
                                  : <AlertCircle className="w-3 h-3 inline mr-1 text-red-400" />}
                                <span className="font-medium">{ln.linea_tipo}</span>
                                {ln.linea_descripcion && <span className="text-zinc-500"> · {ln.linea_descripcion}</span>}
                                {ln.espacio_nombre && (
                                  <span className="ml-2 text-zinc-500 inline-flex items-center gap-0.5">
                                    <MapPin className="w-2.5 h-2.5" />{ln.espacio_nombre} ({ln.ciudad}{ln.tiene_geo === "NO" && <span className="text-red-400"> · sin geo</span>})
                                  </span>
                                )}
                              </div>
                              {ln.razones_excluida && (
                                <div className="text-red-300 mt-0.5 text-[11px] leading-snug">⚠ {ln.razones_excluida}</div>
                              )}
                              <div className="text-zinc-500 text-[10px] mt-0.5">
                                {ln.n_componentes} comp · estado_línea={ln.linea_estado} · {ln.componentes.slice(0, 100)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
